// apps/client/src/components/command-palette/useEntitySearch.ts
// Hook for searching entities (brokers, offices, loans) with debouncing

import { useState, useEffect, useRef } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';

// ===== Types =====

export interface BrokerSearchResult {
  id: string;
  name: string;
  officeName: string;
  status: 'active' | 'inactive';
}

export interface OfficeSearchResult {
  id: string;
  name: string;
  brokerCount: number;
}

export interface LoanSearchResult {
  loanId: string;
  borrowerName: string;
  lender: string;
  loanAmountCents: number;
  settlementDate: string;
}

export interface EntitySearchResults {
  brokers: BrokerSearchResult[];
  offices: OfficeSearchResult[];
  loans: LoanSearchResult[];
  isLoading: boolean;
  error: string | null;
}

interface UseEntitySearchParams {
  query: string;
  companyId: string | null;
  enabled?: boolean;
}

// ===== Search Functions =====

async function searchBrokers(
  companyId: string,
  searchQuery: string
): Promise<BrokerSearchResult[]> {
  const normalizedQuery = searchQuery.toLowerCase().trim();

  // Fetch all brokers (limited) and filter client-side
  // Firestore doesn't support full-text search, so we fetch and filter
  const brokersRef = collection(db, `companies/${companyId}/brokers`);
  const q = query(brokersRef, orderBy('currentName'), limit(50));

  const snapshot = await getDocs(q);

  const results: BrokerSearchResult[] = [];

  snapshot.docs.forEach((doc) => {
    const data = doc.data();

    // Skip merged brokers
    if (data.mergedInto) return;

    const name = data.currentName || data.legalName || 'Unknown';
    const nameMatch = name.toLowerCase().includes(normalizedQuery);

    if (nameMatch) {
      results.push({
        id: doc.id,
        name,
        officeName: data.currentOfficeName || 'Unknown Office',
        status: data.status || 'active',
      });
    }
  });

  return results.slice(0, 5); // Limit to 5 results
}

async function searchOffices(
  companyId: string,
  searchQuery: string
): Promise<OfficeSearchResult[]> {
  const normalizedQuery = searchQuery.toLowerCase().trim();

  const officesRef = collection(db, `companies/${companyId}/offices`);
  const q = query(officesRef, orderBy('name'), limit(20));

  const snapshot = await getDocs(q);

  const results: OfficeSearchResult[] = [];

  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    const name = data.name || 'Unknown Office';
    const nameMatch = name.toLowerCase().includes(normalizedQuery);

    if (nameMatch) {
      results.push({
        id: doc.id,
        name,
        brokerCount: data.brokerCount || 0,
      });
    }
  });

  return results.slice(0, 5); // Limit to 5 results
}

async function searchLoans(searchQuery: string): Promise<LoanSearchResult[]> {
  try {
    const searchLoansFunc = httpsCallable<
      { query: string },
      LoanSearchResult[]
    >(functions, 'searchLoans');

    const result = await searchLoansFunc({ query: searchQuery });
    return result.data;
  } catch (error) {
    // Function might not exist yet - return empty array
    console.warn('[useEntitySearch] searchLoans function not available:', error);
    return [];
  }
}

// ===== Hook =====

export function useEntitySearch({
  query: searchQuery,
  companyId,
  enabled = true,
}: UseEntitySearchParams): EntitySearchResults {
  const [results, setResults] = useState<EntitySearchResults>({
    brokers: [],
    offices: [],
    loans: [],
    isLoading: false,
    error: null,
  });

  // Track request ID to ignore stale responses
  const requestIdRef = useRef(0);

  useEffect(() => {
    // Don't search if disabled, no query, or query too short
    if (!enabled || !searchQuery || searchQuery.trim().length < 2 || !companyId) {
      setResults({
        brokers: [],
        offices: [],
        loans: [],
        isLoading: false,
        error: null,
      });
      return;
    }

    // Increment request ID
    const currentRequestId = ++requestIdRef.current;

    // Set loading immediately
    setResults((prev) => ({ ...prev, isLoading: true, error: null }));

    // Debounce search (300ms)
    const timeoutId = setTimeout(async () => {
      try {
        // Execute all searches in parallel
        const [brokers, offices, loans] = await Promise.all([
          searchBrokers(companyId, searchQuery),
          searchOffices(companyId, searchQuery),
          searchLoans(searchQuery),
        ]);

        // Only update state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          setResults({
            brokers,
            offices,
            loans,
            isLoading: false,
            error: null,
          });
        }
      } catch (err) {
        // Only update error state if this is still the latest request
        if (currentRequestId === requestIdRef.current) {
          console.error('[useEntitySearch] Error searching entities:', err);
          setResults({
            brokers: [],
            offices: [],
            loans: [],
            isLoading: false,
            error: err instanceof Error ? err.message : 'Search failed',
          });
        }
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchQuery, companyId, enabled]);

  return results;
}
